import { useEffect, useState } from 'react';

import MassTextCampaign from '../../../../Model/MassTextCampaign';
import User from '../../../../Model/User';
import { reportError } from '../utils/reportError';

/**
 * Fetches the recent leadCampaign collection for a single `source`, and
 * (optionally) resolves the `createdBy` sender names.
 *
 * @param {Object} params
 * @param {string} params.source leadCampaign source to fetch (MANUAL / REENGAGEMENT_SEQUENCE)
 * @param {boolean} [params.resolveSenders] resolve createdBy -> userNames map
 */
const useCampaignCollection = ({ source, resolveSenders = false }) => {
  const [collection, setCollection] = useState(null);
  const [userNames, setUserNames] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const col = await MassTextCampaign.getRecentBySource(source);
        if (cancelled) return;
        setCollection(col);

        if (!resolveSenders) return;

        const senderIds = Array.from(
          new Set(col.docs.map((doc) => doc.createdBy).filter(Boolean)),
        );
        if (senderIds.length === 0) return;

        const results = await Promise.allSettled(senderIds.map((id) => User.getById(id)));
        if (cancelled) return;
        const names = {};
        results.forEach((result, idx) => {
          if (result.status === 'fulfilled' && result.value) {
            names[senderIds[idx]] = result.value.name;
          }
        });
        setUserNames(names);
      } catch (err) {
        reportError('listCampaigns', err);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [source, resolveSenders]);

  const docs = collection?.docs ?? [];

  return {
    docs,
    userNames,
    loading,
    error,
  };
};

export { useCampaignCollection };
